import React from 'react';
import { motion } from 'framer-motion';
import { 
  Shield, 
  Truck, 
  Wallet,
  Clock
} from 'lucide-react';
import { LABOUR_CHARGES } from '../../../config/constants';
import '../Booking.css';

const OrderSummary = ({ product, quantity }) => {
  const labourCharge = LABOUR_CHARGES[product.weight] || 0;
  const subtotal = product.price * quantity;
  const total = subtotal + labourCharge;

  const summaryItems = [
    { label: 'Product', value: product.name },
    { label: 'Weight', value: product.weight },
    { label: 'Purity', value: product.purity },
    { label: 'Unit Price', value: `₹${product.price.toLocaleString()}` },
    { label: 'Quantity', value: `× ${quantity}` },
    { label: 'Subtotal', value: `₹${subtotal.toLocaleString()}` },
    { label: 'Labour Charge', value: `₹${labourCharge.toLocaleString()}` },
  ];

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        staggerChildren: 0.05,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { type: 'spring', stiffness: 300, damping: 30 },
    },
  };

  return (
    <div className="order-summary-wrapper">
      <motion.h2 
        className="summary-title"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.4 }}
      >
        <Wallet className="summary-title-icon" />
        Order Summary
      </motion.h2>
      
      <motion.div 
        className="summary-card"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {summaryItems.map((item, index) => (
          <motion.div 
            key={index}
            className="summary-item"
            variants={itemVariants}
          >
            <span className="item-label">{item.label}</span>
            <motion.span 
              className="item-value"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 300, delay: index * 0.03 }}
            >
              {item.value}
            </motion.span>
          </motion.div>
        ))}

        <motion.div 
          className="summary-divider"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 0.3 }}
        />
        
        <motion.div 
          className="summary-total"
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ 
            type: 'spring', 
            stiffness: 300, 
            damping: 20,
            delay: 0.4 
          }}
        >
          <span className="total-label">Total Amount</span>
          <motion.span 
            className="total-value"
            animate={{ 
              scale: [1, 1.05, 1],
            }}
            transition={{ 
              duration: 2,
              repeat: Infinity,
              repeatDelay: 3
            }}
          >
            ₹{total.toLocaleString()}
          </motion.span>
        </motion.div>

        <motion.div 
          className="summary-note"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <motion.p
            whileHover={{ x: 4 }}
            transition={{ type: 'spring', stiffness: 400 }}
          >
            <Shield size={16} className="note-icon" />
            Secure transaction
          </motion.p>
          <motion.p
            whileHover={{ x: 4 }}
            transition={{ type: 'spring', stiffness: 400 }}
          >
            <Truck size={16} className="note-icon" />
            100% guaranteed delivery
          </motion.p>
          <motion.p
            whileHover={{ x: 4 }}
            transition={{ type: 'spring', stiffness: 400 }}
          >
            <Clock size={16} className="note-icon" />
            Expected delivery: 3-5 business days
          </motion.p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default OrderSummary;